import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["checked", "disabled", "id", "tabIndex"];
import React, { forwardRef, useRef } from 'react';
import styled, { css } from 'styled-components';
import { alpha, contrast, getDimmedTextColor, pxToRem, _3px } from 'utils/ui';
import useMergedRefs from 'hooks/useMergedRefs';
import { useTabIndexContext } from 'components/TabIndexContext';
import { Ok as OkIcon } from 'components/icons';
var Input = styled.input.withConfig({
  displayName: "Switch__Input",
  componentId: "sc-22vum7-0"
})(["position:absolute;opacity:0;"]);
var Wrapper = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'disabled';
  }
}).withConfig({
  displayName: "Switch__Wrapper",
  componentId: "sc-22vum7-1"
})(["position:relative;display:inline-block;width:2.4rem;height:1.4rem;padding:", ";border:1px solid ", ";border-radius:1.4rem;transition:all 250ms linear;box-sizing:border-box;", " &:focus-within{border-color:", ";}"], pxToRem(2), function (p) {
  return alpha(p.theme.shade, 0.3);
}, function (p) {
  return !p.disabled && css(["&:focus-within,&:hover{background:", ";}"], function (p) {
    return alpha(p.theme.shade, p.theme.shadeStrength);
  });
}, function (p) {
  return p.theme.links;
});
var Handle = styled.span.withConfig({
  displayName: "Switch__Handle",
  componentId: "sc-22vum7-2"
})(["display:flex;height:100%;width:50%;border-radius:100%;overflow:hidden;color:", ";background-color:", ";will-change:transform;transition:transform 150ms ease-out;input:checked + &{background-color:", ";transform:translateX(100%);}input:disabled + &{opacity:0.5;}"], function (p) {
  return contrast(p.theme.links);
}, function (p) {
  return getDimmedTextColor(p.theme);
}, function (p) {
  return p.theme.links;
});
var SwitchIcon = styled(OkIcon).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'checked';
  }
}).withConfig({
  displayName: "Switch__SwitchIcon",
  componentId: "sc-22vum7-3"
})(["position:relative;top:", ";left:", ";opacity:0;transform:translateY(-100%) rotate(45deg);will-change:transform,opacity;transition:transform 0s linear 150ms,opacity 100ms linear;", ""], pxToRem(1), pxToRem(1), function (p) {
  return p.checked && css(["opacity:1;transform:none;transition:all 100ms ease-out 150ms;"]);
});
var FocusRing = styled.span.withConfig({
  displayName: "Switch__FocusRing",
  componentId: "sc-22vum7-4"
})(["opacity:0;input.focus-visible ~ &{position:absolute;top:-", ";left:-", ";bottom:-", ";right:-", ";border-radius:inherit;box-shadow:0 0 0 ", " ", ";opacity:1;transition:opacity 100ms linear;}"], _3px, _3px, _3px, _3px, _3px, function (p) {
  return p.theme.links;
});
var Switch = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    disabled = _ref.disabled,
    id = _ref.id,
    tabIndex = _ref.tabIndex,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef(null);
  var mergedRefs = useMergedRefs([ref, inputRef]);
  var tabIndexContext = useTabIndexContext();
  function forwardClick(e) {
    if (inputRef.current && !inputRef.current.contains(e.currentTarget)) {
      inputRef.current.click();
    }
  }
  return /*#__PURE__*/React.createElement(Wrapper, {
    disabled: disabled,
    onClick: forwardClick
  }, /*#__PURE__*/React.createElement(Input, _extends({
    type: "checkbox",
    ref: mergedRefs,
    id: id,
    checked: checked,
    disabled: disabled,
    tabIndex: tabIndex || tabIndexContext
  }, otherProps)), /*#__PURE__*/React.createElement(Handle, null, /*#__PURE__*/React.createElement(SwitchIcon, {
    scale: 0.8,
    checked: checked
  })), /*#__PURE__*/React.createElement(FocusRing, null));
});
Switch.displayName = 'Switch';
export default Switch;